// components/use-case-details/use-case-workflow/use-case-workflow-hitl-component.tsx

"use client";

import { motion } from 'framer-motion';
import { Activity,CheckCircle, Clock, UserCog, Users, XCircle } from 'lucide-react';
import React from 'react';

import { TechTerm } from '@/components/common/tech-term-system';

interface HITLComponentProps {
  title?: string;
  description?: string;
  approvalProcess: Array<{
    stage: string;
    description: string;
    actor: 'ai' | 'human' | 'system';
    decisionType?: 'binary' | 'rating' | 'edit';
  }>;
  metrics?: Array<{
    name: string;
    value: string;
    description: string;
    trend?: 'up' | 'down' | 'stable';
  }>;
  exampleFeedbackLoop?: {
    aiOutput: string;
    humanFeedback: string;
    improvement: string;
  };
  borderColor?: string;
}

const HITLComponent: React.FC<HITLComponentProps> = ({
  title = "Human-in-the-Loop Workflow",
  description = "A system that combines AI automation with human oversight for better results",
  approvalProcess,
  metrics,
  exampleFeedbackLoop,
  borderColor = "bg-purple-600"
}) => {
  return (
    <motion.div
      className="relative bg-gray-800/50 rounded-lg shadow-lg border-b border-r border-l border-gray-700 mb-6 overflow-hidden flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
      whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
    >
      {/* Top border */}
      <div className={`absolute top-0 left-0 right-0 h-2 ${borderColor}`} />
      
      {/* Card content */}
      <div className="pt-4 flex flex-col px-3 pb-3 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center mb-3 md:mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-700 flex items-center justify-center mb-2 sm:mb-0 sm:mr-3">
            <Users className="text-white" size={20} />
          </div>
          <div>
            <h4 className="text-xl font-bold text-white font-heading">{title}</h4>
            <p className="text-gray-300 text-sm font-body">{description}</p>
          </div>
        </div>

        {/* Approval Process */}
        <div className="mb-4 md:mb-6">
          <h5 className="text-white font-bold mb-3 flex items-center font-heading">
            <Activity className="text-purple-400 mr-2" size={18} />
            <TechTerm term="Human-in-the-Loop">
              Approval Process
            </TechTerm>
          </h5>
          
          <div className="relative">
            {/* Vertical line connecting steps */}
            <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-gray-700 z-0" />
            
            <ol className="space-y-4">
              {approvalProcess.map((step, index) => (
                <li key={index} className="flex items-start relative z-10">
                  {/* Icon based on actor */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                    step.actor === 'ai' ? 'bg-blue-700' : 
                    step.actor === 'human' ? 'bg-purple-700' : 'bg-gray-700'
                  }`}>
                    {step.actor === 'ai' && <UserCog className="text-white" size={16} />}
                    {step.actor === 'human' && <Users className="text-white" size={16} />}
                    {step.actor === 'system' && <Activity className="text-white" size={16} />}
                  </div>
                  
                  <div className="bg-gray-900 p-3 rounded-lg flex-grow">
                    <div className="flex justify-between items-start">
                      <span className="text-white font-medium">{step.stage}</span>
                      <span className="text-xs text-gray-400 capitalize">{step.actor}</span>
                    </div>
                    <p className="text-gray-300 text-sm mt-1">{step.description}</p>
                    
                    {/* Decision type indicator */}
                    {step.decisionType && (
                      <div className="mt-2 flex items-center">
                        <span className="text-xs text-gray-500 mr-2">Decision:</span>
                        {step.decisionType === 'binary' && (
                          <div className="flex space-x-1">
                            <CheckCircle size={14} className="text-green-500" />
                            <XCircle size={14} className="text-red-500" />
                          </div>
                        )}
                        {step.decisionType === 'rating' && (
                          <div className="flex space-x-1">
                            <span className="text-yellow-500">★★★★★</span>
                          </div>
                        )}
                        {step.decisionType === 'edit' && (
                          <div className="flex space-x-1">
                            <span className="text-blue-500 text-xs">Edit & Revise</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        
        {/* Performance Metrics */}
        {metrics && (
          <div className="mb-4 md:mb-6 bg-gray-900 p-3 md:p-4 rounded-lg">
            <h5 className="text-white font-bold mb-3 flex items-center font-heading">
              <Activity className="text-purple-400 mr-2" size={18} />
              Performance Metrics
            </h5>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {metrics.map((metric, index) => (
                <div key={index} className="bg-gray-800 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-400 text-xs">{metric.name}</span>
                    {metric.trend === 'up' && <span className="text-green-500">↑</span>}
                    {metric.trend === 'down' && <span className="text-red-500">↓</span>}
                    {metric.trend === 'stable' && <span className="text-blue-500">→</span>}
                  </div>
                  <div className="text-lg font-bold text-white">{metric.value}</div>
                  <p className="text-xs text-gray-400 mt-1">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Example Feedback Loop */}
        {exampleFeedbackLoop && (
          <div className="bg-gray-900 p-3 md:p-4 rounded-lg">
            <h5 className="text-white font-bold mb-3 flex items-center font-heading">
              <Clock className="text-purple-400 mr-2" size={18} />
              Feedback Loop Example
            </h5>
            
            <div className="space-y-3">
              <div className="bg-gray-800 p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <UserCog size={16} className="text-blue-400 mr-1" />
                  <span className="text-xs text-gray-400">AI Output</span>
                </div>
                <p className="text-gray-300 text-sm">{exampleFeedbackLoop.aiOutput}</p>
              </div>
              
              <div className="bg-gray-800 p-3 rounded-lg border-l-2 border-purple-500">
                <div className="flex items-center mb-1">
                  <Users size={16} className="text-purple-400 mr-1" />
                  <span className="text-xs text-gray-400">Human Feedback</span>
                </div>
                <p className="text-gray-300 text-sm">{exampleFeedbackLoop.humanFeedback}</p>
              </div>
              
              <div className="bg-gray-800 p-3 rounded-lg border-l-2 border-green-500">
                <div className="flex items-center mb-1">
                  <CheckCircle size={16} className="text-green-400 mr-1" />
                  <span className="text-xs text-gray-400">Improvement</span>
                </div>
                <p className="text-gray-300 text-sm">{exampleFeedbackLoop.improvement}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default HITLComponent;
